'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { ChevronDownIcon } from '@heroicons/react/24/outline'

import { locales, type Locale } from '@/i18n/config'
import { localizePathname } from '@/i18n/path'

type Props = {
  locale: string
  names: Record<Locale, string>
  ariaLabel: string
}

export function LocaleSwitcher({ locale, names, ariaLabel }: Props) {
  const current = locales.includes(locale as Locale) ? (locale as Locale) : 'en'
  const pathname = usePathname() ?? `/${current}`
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const listId = useId()
  const close = useCallback(() => setOpen(false), [])

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) close()
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open, close])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, close])

  return (
    <div data-component-id="src/components/home/LocaleSwitcher.tsx:44:4" data-component-path="src/components/home/LocaleSwitcher.tsx" data-component-line="44" data-component-end-line="87" data-component-file="LocaleSwitcher.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%22%7D" ref={rootRef} className="relative">
      <button data-component-id="src/components/home/LocaleSwitcher.tsx:45:6" data-component-path="src/components/home/LocaleSwitcher.tsx" data-component-line="45" data-component-end-line="59" data-component-file="LocaleSwitcher.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%2C%22className%22%3A%22flex%20items-center%20gap-1%20rounded-full%20border%20border-slate-200%20bg-slate-50%20px-2.5%20py-1%20text-%5B11px%5D%20font-semibold%20uppercase%20tracking-wide%20text-ink%20shadow-sm%20transition%20hover%3Aborder-slate-300%20hover%3Abg-white%22%7D"
        type="button"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-haspopup="listbox"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-ink shadow-sm transition hover:border-slate-300 hover:bg-white"
      >
        <span data-component-id="src/components/home/LocaleSwitcher.tsx:54:8" data-component-path="src/components/home/LocaleSwitcher.tsx" data-component-line="54" data-component-end-line="54" data-component-file="LocaleSwitcher.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D">{current}</span>
        <ChevronDownIcon data-component-id="src/components/home/LocaleSwitcher.tsx:55:8" data-component-path="src/components/home/LocaleSwitcher.tsx" data-component-line="55" data-component-end-line="58" data-component-file="LocaleSwitcher.tsx" data-component-name="ChevronDownIcon" data-component-content="%7B%22elementName%22%3A%22ChevronDownIcon%22%7D"
          className={`h-3.5 w-3.5 text-ink-muted transition-transform ${open ? 'rotate-180' : ''}`}
          strokeWidth={2.25}
        />
      </button>

      {open && (
        <div data-component-id="src/components/home/LocaleSwitcher.tsx:62:8" data-component-path="src/components/home/LocaleSwitcher.tsx" data-component-line="62" data-component-end-line="85" data-component-file="LocaleSwitcher.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22id%22%3A%22%5Bvar%3AlistId%5D%22%2C%22className%22%3A%22absolute%20right-0%20top-full%20z-%5B200%5D%20mt-1%20min-w-%5B10rem%5D%20overflow-hidden%20rounded-2xl%20border%20border-slate-200%20bg-white%20py-1%20shadow-lg%22%7D"
          id={listId}
          role="listbox"
          className="absolute right-0 top-full z-[200] mt-1 min-w-[10rem] overflow-hidden rounded-2xl border border-slate-200 bg-white py-1 shadow-lg"
        >
          {locales.map((lng) => {
            const selected = lng === current
            return (
              <Link data-component-id="src/components/home/LocaleSwitcher.tsx:70:14" data-component-path="src/components/home/LocaleSwitcher.tsx" data-component-line="70" data-component-end-line="82" data-component-file="LocaleSwitcher.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%7D"
                key={lng}
                href={localizePathname(pathname, lng)}
                prefetch={false}
                hrefLang={lng === 'rs' ? 'sr' : lng}
                onClick={() => setOpen(false)}
                role="option"
                aria-selected={selected}
                className={`block px-4 py-2.5 text-sm font-medium transition hover:bg-slate-50 ${selected ? 'bg-slate-50 text-brand-purple' : 'text-ink'}`}
              >
                <span data-component-id="src/components/home/LocaleSwitcher.tsx:80:16" data-component-path="src/components/home/LocaleSwitcher.tsx" data-component-line="80" data-component-end-line="80" data-component-file="LocaleSwitcher.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22mr-2%20font-semibold%20uppercase%20text-slate-500%22%7D" className="mr-2 font-semibold uppercase text-slate-500">{lng}</span>
                <span data-component-id="src/components/home/LocaleSwitcher.tsx:81:16" data-component-path="src/components/home/LocaleSwitcher.tsx" data-component-line="81" data-component-end-line="81" data-component-file="LocaleSwitcher.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-ink-muted%22%7D" className="text-ink-muted">{names[lng]}</span>
              </Link>
            )
          })}
        </div>
      )}
    </div>
  )
}
